import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

/**
 * GLTF/GLB Parser Utility
 * Extracts component names from 3D model files - specifically from materials section
 */

/**
 * Extract component names (unique material names) from a GLTF/GLB model by traversing the scene.
 * This matches the logic of the 3D ViewerCanvas for identifying unique components.
 * @param {string} modelUrl - URL to the GLTF/GLB model file
 * @returns {Promise<Array<string>>} - Array of unique component names from materials
 */
export const extractComponentNamesFromModel = async (modelUrl) => {
  if (!modelUrl) {
    console.warn('❌ extractComponentNamesFromModel: No modelUrl provided.');
    return [];
  }

  // Pre-flight check: Verify if the modelUrl returns a valid JSON or binary gltf/glb,
  // and NOT an HTML error page (e.g. <!DOCTYPE html> from 404 spa routing)
  try {
    const headRes = await fetch(modelUrl, { method: 'GET', headers: { 'Range': 'bytes=0-100' } });
    if (!headRes.ok) {
      console.warn('❌ extractComponentNamesFromModel: Pre-flight fetch returned error status:', headRes.status);
      return [];
    }
    const textSample = await headRes.text();
    if (textSample.toLowerCase().includes('<!doctype html') || textSample.toLowerCase().includes('<html')) {
      console.warn('❌ extractComponentNamesFromModel: Pre-flight check failed! URL returned HTML instead of GLTF/GLB binary:', modelUrl);
      return [];
    }
  } catch (err) {
    console.warn('❌ extractComponentNamesFromModel: Pre-flight connection failed:', err);
  }

  try {
    const loader = new GLTFLoader();
    
    return new Promise((resolve, reject) => {
      loader.load(
        modelUrl,
        (gltf) => {
          const uniqueComponentNames = new Set(); // Use a Set to store unique names
          
          console.log('🔍 GLTF Object loaded in extractComponentNamesFromModel:', {
            hasMaterialsJson: !!gltf?.parser?.json?.materials, // For debug comparison
            hasScene: !!gltf?.scene
          });

          let counter = 1;
          if (gltf?.scene) {
            gltf?.scene?.traverse((node) => {
              if (node?.isMesh) {
                const matName = Array.isArray(node.material) ? node.material[0]?.name : node.material?.name;
                const componentDisplayName = (node.name && node.name.trim()) || (matName && matName.trim()) || `Mesh ${counter}`;

                if (componentDisplayName) {
                  uniqueComponentNames?.add(componentDisplayName);
                }
                counter++;
              }
            });
          }
          
          // Convert Set to sorted array of unique names
          const sortedNames = Array.from(uniqueComponentNames)?.sort();
          console.log('✅ Extracted unique component names from parser (scene traversal):', sortedNames, 'Count:', sortedNames?.length);
          resolve(sortedNames);
        },
        // onProgress callback (optional)
        undefined,
        (error) => {
          console.error('❌ Error loading GLTF model in extractComponentNamesFromModel:', error);
          reject(error);
        }
      );
    });
  } catch (error) {
    console.error('❌ GLTF parsing error in extractComponentNamesFromModel (outer catch):', error);
    return [];
  }
};

/**
 * Extract detailed component information from a GLTF/GLB model
 * This function is designed to match the component counting logic of the 3D ViewerCanvas.
 * It counts each distinct mesh instance that has a non-auto-generated material name.
 * @param {string} modelUrl - URL to the GLTF/GLB model file
 * @returns {Promise<Array<Object>>} - Array of component objects with metadata
 */
export const extractDetailedComponentsFromModel = async (modelUrl) => {
  if (!modelUrl) {
    console.warn('❌ extractDetailedComponentsFromModel: No modelUrl provided.');
    return [];
  }

  try {
    const loader = new GLTFLoader();
    
    return new Promise((resolve, reject) => {
      loader.load(
        modelUrl,
        (gltf) => {
          const components = [];
          let componentIdCounter = 1;

          console.log('🔍 GLTF Object loaded in extractDetailedComponentsFromModel:', {
            hasMaterialsJson: !!gltf?.parser?.json?.materials,
            hasScene: !!gltf?.scene
          });
          
          // CRITICAL FIX: Traverse the scene to count individual mesh instances,
          // matching the ViewerCanvas behavior. This counts ALL meshes with valid material names,
          // not just unique material names.
          if (gltf?.scene) { // Ensure scene exists before traversing
            gltf?.scene?.traverse((node) => {
              if (node?.isMesh) {
                const matName = Array.isArray(node.material) ? node.material[0]?.name : node.material?.name;
                const componentDisplayName = (node.name && node.name.trim()) || (matName && matName.trim()) || `Mesh ${componentIdCounter}`;

                // Push a component for each distinct mesh instance
                // The `name` property should be the most descriptive name found
                components?.push({
                  id: `component-${componentIdCounter}`,
                  name: componentDisplayName, // Use the determined descriptive name
                  type: node?.type, // e.g., "Mesh"
                  group: node?.parent?.name || 'main', // Group by parent node name
                  material: matName, // Store material name
                  hasTexture: !!(node?.material?.map), // Check if material has a texture map
                  position: {
                    x: node?.position?.x,
                    y: node?.position?.y,
                    z: node?.position?.z
                  }
                });
                
                componentIdCounter++;
              }
            });
          }
          
          console.log('✅ Extracted detailed components from parser (mesh instances):', components, 'Count:', components?.length);
          resolve(components);
        },
        // onProgress callback (optional)
        undefined,
        (error) => {
          console.error('❌ Error loading GLTF model in extractDetailedComponentsFromModel:', error);
          reject(error);
        }
      );
    });
  } catch (error) {
    console.error('❌ GLTF parsing error in extractDetailedComponentsFromModel (outer catch):', error);
    return [];
  }
};